import { RecentlyViewed } from '@theme/recently-viewed-products';

class YeisiProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-tab]')];
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.dots = this.querySelector('[data-carousel-dots]');
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.selectTab(tab.dataset.tab)));
    this.disableNestedImageCarousels();
    this.loadRecentProducts();
    requestAnimationFrame(() => requestAnimationFrame(() => this.setupDots()));
  }

  disableNestedImageCarousels() {
    this.querySelectorAll('.card-gallery slideshow-component').forEach((slideshow) => {
      slideshow.setAttribute('disabled', 'true');
      slideshow.querySelectorAll('slideshow-slide').forEach((slide, index) => {
        if (index > 0) slide.hidden = true;
      });
    });
  }

  selectTab(name) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.tab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });

    this.panels.forEach((panel) => {
      const active = panel.dataset.panel === name;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });

    requestAnimationFrame(() => this.setupDots());
  }

  async loadRecentProducts() {
    const currentId = String(this.dataset.currentProduct || '');
    const ids = RecentlyViewed.getProducts().filter((id) => String(id) !== currentId);
    const container = this.querySelector('[data-recent-products]');
    const empty = this.querySelector('[data-recent-empty]');

    if (!container || !ids.length) {
      if (empty) empty.hidden = false;
      return;
    }

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('section_id', 'predictive-search');

      const markup = await fetch(url).then((response) => response.text());
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      const handles = [...documentFragment.querySelectorAll('#predictive-search-products a[href*="/products/"]')]
        .map((link) => new URL(link.href, location.origin).pathname.match(/\/products\/([^/?#]+)/)?.[1])
        .filter(Boolean);
      const uniqueHandles = [...new Set(handles)].slice(0, 4);
      const products = await Promise.all(
        uniqueHandles.map((handle) =>
          fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`).then((response) => response.json())
        )
      );

      if (!products.length) throw new Error('No recently viewed products');
      container.replaceChildren(...products.map((product) => this.createCard(product)));
      if (empty) empty.hidden = true;
      if (this.querySelector('[data-panel="recent"]')?.classList.contains('is-active')) this.setupDots();
    } catch (error) {
      if (empty) empty.hidden = false;
    }
  }

  createCard(product) {
    const card = document.createElement('article');
    card.className = 'yeisi-recent-card';
    const availableVariant = product.variants.find((variant) => variant.available);
    const comparePrice = Number(product.compare_at_price || 0);
    const price = Number(product.price || 0);
    const discount = comparePrice > price ? Math.round((1 - price / comparePrice) * 100) : 0;
    const image = product.featured_image || product.images?.[0] || '';
    const productUrl = product.url || `/products/${product.handle}`;

    card.innerHTML = `
      <a class="yeisi-recent-card__media" href="${this.escape(productUrl)}">
        ${discount ? `<span class="yeisi-recent-card__discount">-${discount}%</span>` : ''}
        <img src="${this.escape(image)}" alt="${this.escape(product.title)}" loading="lazy">
      </a>
      ${
        availableVariant
          ? `<form class="yeisi-recent-card__form" method="post" action="${window.Shopify?.routes?.root || '/'}cart/add">
              <input type="hidden" name="id" value="${availableVariant.id}">
              <button type="submit">Agregar al carrito</button>
            </form>`
          : ''
      }
      <a class="yeisi-recent-card__title" href="${this.escape(productUrl)}">${this.escape(product.title)}</a>
      <div class="yeisi-recent-card__prices">
        ${comparePrice > price ? `<span class="yeisi-recent-card__compare">${this.money(comparePrice)}</span>` : ''}
        <span class="yeisi-recent-card__price">${this.money(price)}</span>
      </div>
    `;
    return card;
  }

  setupDots() {
    if (!this.dots || !window.matchMedia('(max-width: 749px)').matches) return;
    const activePanel = this.querySelector('[data-panel].is-active');
    const scroller = activePanel?.querySelector('.resource-list--grid, [data-recent-products]');
    if (!scroller) {
      this.dots.replaceChildren();
      return;
    }

    const cards = [...scroller.children].filter((element) => !element.hidden);
    const pages = Math.max(1, Math.ceil(cards.length / 2));
    this.dots.replaceChildren();
    for (let index = 0; index < pages; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `yeisi-product-tabs__dot${index === 0 ? ' is-active' : ''}`;
      dot.setAttribute('aria-label', `Ir al grupo ${index + 1}`);
      dot.addEventListener('click', () => cards[index * 2]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' }));
      this.dots.append(dot);
    }
    this.dots.hidden = cards.length < 1;

    scroller.onscroll = () => {
      const page = Math.min(pages - 1, Math.round(scroller.scrollLeft / Math.max(scroller.clientWidth, 1)));
      [...this.dots.children].forEach((dot, index) => dot.classList.toggle('is-active', index === page));
    };
  }

  money(cents) {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'COP',
      maximumFractionDigits: 0,
    }).format(cents / 100);
  }

  escape(value) {
    const element = document.createElement('div');
    element.textContent = String(value || '');
    return element.innerHTML;
  }
}

if (!customElements.get('yeisi-product-tabs')) {
  customElements.define('yeisi-product-tabs', YeisiProductTabs);
}
